import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    ScrollView,
    TouchableOpacity,
    TextInput,
    Image,
    StyleSheet,
    ActivityIndicator,
    Animated,
    Dimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useCart } from '../../context/CartContext';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { H2, H3, Caption } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';

import { getFeaturedBusinesses, getMarketplaceCategories, searchBusinesses } from '../../api/marketplace';
import MarketplaceFooter from '../../components/common/MarketplaceFooter';
import { getImageUrl } from '../../utils/imageUtils';
import Skeleton from '../../components/common/Skeleton';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const CATEGORY_THEMES = {
    'Food': { icon: 'restaurant', color: '#EF4444' },
    'Fashion': { icon: 'shirt', color: '#EC4899' },
    'Technology': { icon: 'laptop', color: '#6366F1' },
    'Tech': { icon: 'laptop', color: '#6366F1' },
    'Agriculture': { icon: 'leaf', color: '#10B981' },
    'Agri': { icon: 'leaf', color: '#10B981' },
    'Crafts': { icon: 'color-palette', color: '#F59E0B' },
    'Services': { icon: 'construct', color: '#8B5CF6' }
};

const MarketplaceScreen = ({ navigation, route }) => {
    const insets = useSafeAreaInsets();
    const scrollY = useRef(new Animated.Value(0)).current;
    const { getCartCount } = useCart();
    const [categories, setCategories] = useState([]);
    const [featuredListings, setFeaturedListings] = useState([]);
    const [searchQuery, setSearchQuery] = useState(route.params?.category || '');
    const [loading, setLoading] = useState(true);
    const [activeMainTab, setActiveMainTab] = useState('Shops'); // Shops or Services

    useEffect(() => {
        fetchData();
    }, [route.params?.category]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const cats = await getMarketplaceCategories();
            setCategories(cats);

            let businesses;
            if (searchQuery) {
                businesses = await searchBusinesses(searchQuery);
            } else {
                businesses = await getFeaturedBusinesses();
            }
            setFeaturedListings(businesses);
        } catch (error) {
            console.error('Error fetching marketplace data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            fetchData();
            return;
        }
        try {
            setLoading(true);
            const results = await searchBusinesses(searchQuery);
            setFeaturedListings(results);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryPress = (category) => {
        setSearchQuery(category.name);
        handleSearch();
    };

    const handleListingPress = (listing) => {
        navigation.navigate('BusinessDetail', { listingId: listing.id });
    };

    const resetView = () => {
        setSearchQuery('');
        fetchData();
    };

    const headerHeight = 180;
    const headerOpacity = scrollY.interpolate({
        inputRange: [0, 60],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const stickyHeaderOpacity = scrollY.interpolate({
        inputRange: [60, 100],
        outputRange: [0, 1],
        extrapolate: 'clamp',
    });

    return (
        <View style={styles.container}>
            <StatusBar style="light" />

            <View style={[styles.headerBackground, { height: headerHeight + 40 }]}>
                <LinearGradient
                    colors={['#7c3aed', '#6366f1', '#4f46e5']}
                    style={StyleSheet.absoluteFill}
                />
                <LinearGradient
                    colors={['rgba(255,255,255,0.25)', 'rgba(255,255,255,0.05)', 'transparent']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 0.6, y: 0.6 }}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.auraContainer}>
                    <View style={[styles.aura, styles.aura1]} />
                    <View style={[styles.aura, styles.aura2]} />
                    <View style={[styles.aura, styles.aura3]} />
                </View>
            </View>

            <Animated.View
                style={[
                    styles.miniStickyHeader,
                    { paddingTop: insets.top + 5, opacity: stickyHeaderOpacity }
                ]}
                pointerEvents="box-none"
            >
                <LinearGradient
                    colors={['#6366f1', '#4f46e5']}
                    style={StyleSheet.absoluteFill}
                    pointerEvents="none"
                />
                <View style={styles.miniHeaderContent}>
                    <H2 style={styles.miniHeaderTitle}>Marketplace</H2>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
                        <Ionicons name="chevron-back" size={18} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
            </Animated.View>

            <Animated.ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollViewContent}
                showsVerticalScrollIndicator={false}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: true }
                )}
                scrollEventThrottle={16}
            >
                <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 15, opacity: headerOpacity }]}>
                    <View style={styles.topBar}>
                        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backCircle}>
                            <Ionicons name="arrow-back" size={20} color="#FFFFFF" />
                        </TouchableOpacity>
                        <View style={styles.headerIcons}>
                            <TouchableOpacity
                                style={styles.iconCircle}
                                onPress={() => navigation.navigate('Cart')}
                            >
                                <Ionicons name="cart-outline" size={20} color="#FFFFFF" />
                                {getCartCount() > 0 && (
                                    <View style={styles.cartBadge}>
                                        <Caption style={styles.cartBadgeText}>{getCartCount()}</Caption>
                                    </View>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>

                    <View style={styles.heroTitleBlock}>
                        <Caption style={styles.heroSubtitle}>SHOP • CONNECT</Caption>
                        <H2 style={styles.heroTitle}>Youth Marketplace</H2>
                    </View>

                    <View style={styles.statsGridCompact}>
                        <View style={styles.statPill}>
                            <Ionicons name="business" size={12} color="#FFFFFF" />
                            <Caption style={styles.statPillText}>{featuredListings.length}+ Listings</Caption>
                        </View>
                        <View style={styles.statPill}>
                            <Ionicons name="shield-checkmark" size={12} color="#FFFFFF" />
                            <Caption style={styles.statPillText}>Verified Shops</Caption>
                        </View>
                    </View>
                </Animated.View>

                <View style={styles.contentSurface}>
                    <View style={styles.searchSection}>
                        <View style={styles.searchContainer}>
                            <Ionicons name="search" size={18} color="#9CA3AF" />
                            <TextInput
                                style={styles.searchInput}
                                placeholder="Search stores..."
                                value={searchQuery}
                                onChangeText={setSearchQuery}
                                onSubmitEditing={handleSearch}
                                placeholderTextColor="#9CA3AF"
                            />
                            {searchQuery.length > 0 && (
                                <TouchableOpacity onPress={resetView}>
                                    <Ionicons name="close-circle" size={18} color="#9CA3AF" />
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    <View style={styles.quickActionsRow}>
                        {[
                            { icon: 'add-circle', label: 'List Store', route: 'CreateBusiness', color: '#F59E0B' },
                            { icon: 'briefcase', label: 'My Biz', route: 'MyBusinesses', color: '#7C3AED' },
                            { icon: 'receipt', label: 'Orders', route: 'OrderHistory', color: '#6366F1' },
                            { icon: 'wallet', label: 'Wallet', route: 'Wallet', color: '#10B981' },
                        ].map((action, idx) => (
                            <TouchableOpacity
                                key={idx}
                                style={styles.actionItem}
                                onPress={() => navigation.navigate(action.route)}
                            >
                                <View style={[styles.actionIconWrap, { backgroundColor: action.color + '10' }]}>
                                    <Ionicons name={action.icon} size={20} color={action.color} />
                                </View>
                                <Caption style={styles.actionLabel}>{action.label}</Caption>
                            </TouchableOpacity>
                        ))}
                    </View>

                    {/* Main Category Tabs */}
                    <View style={styles.mainTabsWrap}>
                        <View style={styles.mainTabs}>
                            {['Shops', 'Services'].map(tab => (
                                <TouchableOpacity
                                    key={tab}
                                    style={[styles.mainTab, activeMainTab === tab && styles.mainTabActive]}
                                    onPress={() => {
                                        setActiveMainTab(tab);
                                        if (tab === 'Services') {
                                            setSearchQuery('Services');
                                        } else {
                                            setSearchQuery('');
                                        }
                                    }}
                                >
                                    <Text style={[styles.mainTabText, activeMainTab === tab && styles.mainTabTextActive]}>{tab}</Text>
                                    {activeMainTab === tab && <View style={styles.tabIndicator} />}
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>

                    {!searchQuery && featuredListings.length > 0 && (
                        <>
                            <View style={styles.sectionHeader}>
                                <H3 style={styles.surfaceTitle}>Recommended Stores</H3>
                            </View>
                            <View style={styles.bannerContainer}>
                                <ScrollView
                                    horizontal
                                    showsHorizontalScrollIndicator={false}
                                    contentContainerStyle={{ paddingHorizontal: 18 }}
                                >
                                    {loading ? (
                                        [1, 2].map(i => (
                                          <Skeleton key={i} width={SCREEN_WIDTH - 40} height={120} borderRadius={16} style={{ marginRight: 10 }} />
                                        ))
                                    ) : (
                                        featuredListings.slice(0, 5).sort(() => 0.5 - Math.random()).slice(0, 3).map((store, index) => (
                                            <TouchableOpacity
                                                key={store.id}
                                                style={styles.bannerCard}
                                                onPress={() => handleListingPress(store)}
                                            >
                                                <Image
                                                    source={{ uri: getImageUrl(store.logo_url) || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=500' }}
                                                    style={styles.bannerImage}
                                                />
                                                <LinearGradient
                                                    colors={['transparent', 'rgba(0,0,0,0.7)']}
                                                    style={styles.bannerOverlay}
                                                >
                                                    <View style={styles.bannerTag}>
                                                        <Caption style={styles.bannerTagText}>
                                                            {index === 0 ? '✨ TRENDING' : index === 1 ? '🔥 TOP RATED' : '💎 FEATURED'}
                                                        </Caption>
                                                    </View>
                                                    <H2 style={styles.bannerTitle} numberOfLines={1}>{store.business_name}</H2>
                                                    <View style={styles.bannerFooter}>
                                                        <View style={styles.bannerStat}>
                                                            <Ionicons name="star" size={10} color="#F59E0B" />
                                                            <Caption style={styles.bannerStatText}>{store.rating || '5.0'}</Caption>
                                                        </View>
                                                        <View style={styles.bannerDot} />
                                                        <Caption style={styles.bannerStatText}>{store.location || 'Local Shop'}</Caption>
                                                    </View>
                                                </LinearGradient>
                                            </TouchableOpacity>
                                        ))
                                    )}
                                </ScrollView>
                            </View>
                        </>
                    )}

                    <View style={styles.listingsSection}>
                        <View style={styles.sectionHeaderLine}>
                            <H3 style={styles.surfaceTitle}>{searchQuery ? `Results for "${searchQuery}"` : 'Discover Stores'}</H3>
                            {searchQuery ? (
                                <TouchableOpacity onPress={resetView}>
                                    <Caption style={styles.clearText}>Clear</Caption>
                                </TouchableOpacity>
                            ) : null}
                        </View>

                        {loading ? (
                            <View style={styles.grid}>
                                {[1, 2, 3, 4].map(i => (
                                    <View key={i} style={styles.businessCard}>
                                        <Skeleton width="100%" height={150} borderRadius={16} />
                                    </View>
                                ))}
                            </View>
                        ) : (
                            featuredListings.length === 0 ? (
                                <View style={styles.emptyContainer}>
                                    <Ionicons name="storefront-outline" size={48} color="#D1D5DB" />
                                    <H3 style={styles.emptyTitle}>No stores matching your search</H3>
                                    <Caption style={styles.emptyDesc}>Try a different search term or explore categories</Caption>
                                </View>
                            ) : (
                                <View style={styles.grid}>
                                    {featuredListings.map((listing) => (
                                        <TouchableOpacity
                                            key={listing.id}
                                            style={styles.businessCard}
                                            onPress={() => handleListingPress(listing)}
                                        >
                                            <View style={styles.businessCardInner}>
                                                {listing.logo_url ? (
                                                    <Image source={{ uri: getImageUrl(listing.logo_url) }} style={styles.businessLogo} />
                                                ) : (
                                                    <View style={styles.businessLogoPlaceholder}>
                                                        <Ionicons name="image-outline" size={30} color="#E5E7EB" />
                                                    </View>
                                                )}
                                                <View style={styles.businessInfo}>
                                                    <H3 style={styles.businessName} numberOfLines={1}>{listing.business_name}</H3>
                                                    <View style={styles.businessMeta}>
                                                        <Ionicons name="star" size={10} color="#F59E0B" />
                                                        <Text style={styles.ratingText}>{listing.rating || '5.0'}</Text>
                                                        <View style={styles.metaDot} />
                                                        <Text style={styles.locationText} numberOfLines={1}>{listing.location || 'Local'}</Text>
                                                    </View>
                                                    <View style={styles.tagRow}>
                                                        <View style={styles.tag}>
                                                            <Text style={styles.tagText}>{listing.category?.name || 'General'}</Text>
                                                        </View>
                                                    </View>
                                                </View>
                                            </View>
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            )
                        )}
                    </View>

                    {/* Top Categories Strip - Moved to Bottom per User Request */}
                    {!searchQuery && categories.length > 0 && (
                        <>
                            <View style={[styles.sectionHeader, { marginTop: 10 }]}>
                                <H3 style={styles.surfaceTitle}>Top Categories</H3>
                            </View>
                            <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={styles.categoriesScroll}
                            >
                                {categories.map((cat) => {
                                    const theme = CATEGORY_THEMES[cat.name] || { icon: 'basket', color: '#64748B' };
                                    return (
                                        <TouchableOpacity
                                            key={cat.id}
                                            style={styles.catCard}
                                            onPress={() => handleCategoryPress(cat)}
                                        >
                                            <View style={[styles.catIconWrap, { backgroundColor: theme.color + '15' }]}>
                                                <Ionicons name={theme.icon} size={20} color={theme.color} />
                                            </View>
                                            <Caption style={styles.catLabel} numberOfLines={1}>{cat.name}</Caption>
                                        </TouchableOpacity>
                                    );
                                })}
                            </ScrollView>
                        </>
                    )}

                    <View style={styles.footerSection}>
                        <Caption style={styles.footerText}>Connect with local entrepreneurs • Youth Connect</Caption>
                    </View>
                </View>
            </Animated.ScrollView>
            <MarketplaceFooter activeTab="Home" navigation={navigation} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    headerBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 0,
    },
    auraContainer: {
        ...StyleSheet.absoluteFillObject,
        overflow: 'hidden',
    },
    aura: {
        position: 'absolute',
        borderRadius: 150,
        opacity: 0.15,
    },
    aura1: {
        width: 300,
        height: 300,
        left: -100,
        top: -50,
        backgroundColor: '#C084FC',
    },
    aura2: {
        width: 250,
        height: 250,
        right: -50,
        top: -20,
        backgroundColor: '#818CF8',
    },
    aura3: {
        width: 200,
        height: 200,
        right: 30,
        bottom: -60,
        backgroundColor: '#4C1D95',
    },
    miniStickyHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 90,
        elevation: 4,
    },
    miniHeaderContent: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        height: 44,
        marginTop: 20,
    },
    miniHeaderTitle: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
    miniBackBtn: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollView: {
        flex: 1,
        zIndex: 1,
    },
    scrollViewContent: {
        paddingBottom: 20,
    },
    heroHeader: {
        paddingHorizontal: 20,
        paddingBottom: 25,
        zIndex: 2,
    },
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    backCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.25)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerIcons: {
        flexDirection: 'row',
        gap: 12,
    },
    iconCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cartBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: '#EF4444', // Red badge for contrast
        width: 16,
        height: 16,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    cartBadgeText: {
        color: '#FFFFFF',
        fontSize: 8,
        fontWeight: '900',
    },
    heroTitleBlock: {
        marginBottom: 8,
    },
    heroSubtitle: {
        color: 'rgba(255,255,255,0.8)',
        letterSpacing: 2,
        fontWeight: '800',
        fontSize: 10,
    },
    heroTitle: {
        color: '#FFFFFF',
        fontSize: 26,
        fontWeight: '800',
        marginTop: 2,
    },
    statsGridCompact: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 5,
    },
    statPill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.15)',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
        gap: 4,
    },
    statPillText: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 10,
    },
    contentSurface: {
        backgroundColor: '#F9FAFB',
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        paddingTop: 24,
        minHeight: SCREEN_HEIGHT - 100,
        zIndex: 5,
        marginTop: -15,
    },
    searchSection: {
        paddingHorizontal: 20,
        marginBottom: 20,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        paddingHorizontal: 12,
        height: 44,
        ...getShadow('sm'),
    },
    searchInput: {
        flex: 1,
        marginLeft: 8,
        fontSize: 14,
        fontWeight: '600',
        color: '#111827',
    },
    quickActionsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        marginBottom: 24,
    },
    actionItem: {
        alignItems: 'center',
        flex: 1,
    },
    actionIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 6,
    },
    actionLabel: {
        fontSize: 10,
        fontWeight: '800',
        color: '#64748B',
    },
    mainTabsWrap: {
        paddingHorizontal: 20,
        marginBottom: 20,
    },
    mainTabs: {
        flexDirection: 'row',
        gap: 30,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    mainTab: {
        paddingBottom: 10,
        position: 'relative',
    },
    mainTabText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#9CA3AF',
    },
    mainTabTextActive: {
        color: '#4F46E5',
    },
    tabIndicator: {
        position: 'absolute',
        bottom: -1,
        left: 0,
        right: 0,
        height: 2,
        backgroundColor: '#4F46E5',
        borderRadius: 2,
    },
    sectionHeader: {
        paddingHorizontal: 20,
        marginBottom: 12,
    },
    surfaceTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
    },
    categoriesScroll: {
        paddingHorizontal: 20,
        paddingBottom: 24,
    },
    catCard: {
        alignItems: 'center',
        marginRight: 15,
        width: 60,
    },
    catIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 6,
    },
    catLabel: {
        fontSize: 11,
        fontWeight: '700',
        color: '#64748B',
    },
    listingsSection: {
        paddingHorizontal: 18,
        paddingBottom: 20,
    },
    bannerContainer: {
        marginBottom: 24,
    },
    bannerCard: {
        width: SCREEN_WIDTH - 40,
        height: 120,
        borderRadius: 16,
        marginRight: 10,
        overflow: 'hidden',
        ...getShadow('md'),
    },
    bannerImage: {
        width: '100%',
        height: '100%',
    },
    bannerOverlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'flex-end',
        padding: 12,
    },
    bannerTag: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignSelf: 'flex-start',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        marginBottom: 6,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    bannerTagText: {
        color: '#FFFFFF',
        fontWeight: '900',
        fontSize: 8,
    },
    bannerTitle: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '800',
        marginBottom: 2,
    },
    bannerFooter: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    bannerStat: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
    },
    bannerStatText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 10,
        fontWeight: '700',
    },
    bannerDot: {
        width: 2,
        height: 2,
        borderRadius: 1,
        backgroundColor: 'rgba(255,255,255,0.5)',
        marginHorizontal: 6,
    },
    sectionHeaderLine: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    clearText: {
        color: '#6366F1',
        fontWeight: '800',
        fontSize: 12,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    businessCard: {
        width: '48%',
        marginBottom: 12,
    },
    businessCardInner: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        overflow: 'hidden',
        ...getShadow('xs'),
    },
    businessLogo: {
        width: '100%',
        height: 100,
        backgroundColor: '#F3F4F6',
    },
    businessLogoPlaceholder: {
        width: '100%',
        height: 100,
        backgroundColor: '#F8FAFB',
        alignItems: 'center',
        justifyContent: 'center',
    },
    businessInfo: {
        padding: 10,
    },
    businessName: {
        fontSize: 13,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 2,
    },
    businessMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 6,
    },
    ratingText: {
        fontSize: 10,
        fontWeight: '700',
        color: '#111827',
        marginLeft: 3,
    },
    metaDot: {
        width: 2,
        height: 2,
        borderRadius: 1,
        backgroundColor: '#D1D5DB',
        marginHorizontal: 6,
    },
    locationText: {
        fontSize: 11,
        color: '#6B7280',
        flex: 1,
    },
    tagRow: {
        flexDirection: 'row',
    },
    tag: {
        backgroundColor: '#F3F4F6',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
    },
    tagText: {
        fontSize: 9,
        fontWeight: '800',
        color: '#6B7280',
    },
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: 40,
    },
    emptyTitle: {
        marginTop: 12,
        color: '#374151',
        fontSize: 15,
        textAlign: 'center',
    },
    emptyDesc: {
        color: '#6B7280',
        textAlign: 'center',
        marginTop: 4,
        paddingHorizontal: 16,
        fontSize: 13,
    },
    footerSection: {
        alignItems: 'center',
        paddingVertical: 40,
        paddingBottom: 20,
    },
    footerText: {
        color: '#94A3B8',
        fontSize: 11,
        fontWeight: '600',
    }
});

export default MarketplaceScreen;
